import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Snackbar,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useAppStore } from "../../../../appStore";
import { api_url } from "../../../../apiUtils";
import TransferSuccessDialog from "./successDilaog";
import { toast } from "react-toastify";
import CheckIcon from "@mui/icons-material/Check";
import OtpVerificationDialog from "./OtpVerificationDialog";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function OnlineTransferSimple() {
  const theme = useTheme();
  const isCompactTransferView = useMediaQuery(theme.breakpoints.down("md"));
  const [recipientMobile, setRecipientMobile] = useState("");
  const [reEnteredRecipientMobile, setReEnteredRecipientMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [receiveCash, setReceiveCash] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [otpSent, setOtpSent] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [isValidMobile, setIsValidMobile] = useState(false);

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(900);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatTime = (seconds) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${secs}`;
  };

  const [otpSentTime, setOtpSentTime] = useState(null);

  const getRemainingOtpTime = () => {
    if (!otpSentTime) return null;
    const now = Date.now();
    const diffMs = 15 * 60 * 1000 - (now - otpSentTime);
    if (diffMs <= 0) return "Expired";
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };
  const [otpRemainingText, setOtpRemainingText] = useState("");

  useEffect(() => {
    if (!otpSentTime) return;

    const updateOtpTimer = () => {
      const timeLeft = getRemainingOtpTime();
      setOtpRemainingText(timeLeft);
      return timeLeft;
    };

    updateOtpTimer();
    const interval = setInterval(() => {
      if (updateOtpTimer() === "Expired") clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpSentTime]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (otpSent) {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave? OTP will be invalidated.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [otpSent]);

  const [amountError, setAmountError] = useState("");
  const otpFee = 10; // Or fetch dynamically if needed

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (validateAmount(val)) {
      const numVal = Number(val);
      if (!val) {
        setAmount("");
        setAmountError("");
      } else if (walletBalance < numVal + otpFee) {
        setAmount(val);
        setAmountError(
          "Wallet balance is too low for this transfer + transaction fee + OTP charge .",
        );
      } else {
        setAmount(val);
        setAmountError("");
      }
    }
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { profileData, profileDetails, setWalletBalance, walletBalance } =
    useAppStore();
  const isHwlMobileTransferView =
    isCompactTransferView && profileData?.userLoginType === "HWL";
  const senderMobile = profileDetails?.primaryPhone?.contactValue;
  // const senderMobile = 8073135867;

  const inputRefs = useRef([]);

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const validateMobileNumber = (value) => /^[0-9]{0,10}$/.test(value);
  const validateAmount = (value) => /^[0-9]*$/.test(value);

  const checkMobileExists = async (number) => {
    if (number === senderMobile) {
      setMobileError("You cannot request points to your own number.");
      setIsValidMobile(false);
      return;
    }

    try {
      await axios.get(`${api_url}wallet/check-mobilenumber/${number}`);
      setMobileError("");
      setIsValidMobile(true);
    } catch (error) {
      const msg = error.response?.data?.message || "Validation failed.";
      setMobileError(msg);
      setIsValidMobile(false);
    }
  };

  useEffect(() => {
    if (recipientMobile.length === 10) {
      checkMobileExists(recipientMobile);
    } else {
      setMobileError("");
      setIsValidMobile(false);
    }
  }, [recipientMobile]);

  // Send OTP API call
  const sendOtp = async () => {
    if (!senderMobile || senderMobile.toString().length !== 10) {
      setSnackbar({
        open: true,
        message: "Invalid sender mobile number.",
        severity: "error",
      });
      return;
    }

    if (recipientMobile === senderMobile) {
      setSnackbar({
        open: true,
        message: "Sender and recipient numbers cannot be the same.",
        severity: "error",
      });
      return;
    }

    if (recipientMobile.length !== 10) {
      setSnackbar({
        open: true,
        message: "Enter valid 10-digit recipient number.",
        severity: "error",
      });
      return;
    }

    if (recipientMobile !== reEnteredRecipientMobile) {
      setSnackbar({
        open: true,
        message: "Recipient mobile numbers do not match.",
        severity: "error",
      });
      return;
    }

    if (!amount || Number(amount) <= 0) {
      setSnackbar({
        open: true,
        message: "Enter valid amount.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${api_url}otp/send-otp`,
        {
          mobileNumber: senderMobile,
        },
        {
          headers: {
            accesstoken: sessionStorage.getItem("accesstoken"),
          },
        },
      );

      if (res.data.success) {
        setOtpSent(true);
        setOtpId(res.data.otpId || null);
        getWalletPoints();
        // setResendCooldown(900); // start 30 second cooldown
        setOtpSentTime(Date.now());

        setSnackbar({
          open: true,
          message: "OTP sent to your mobile number!",
          severity: "success",
        });
        setOtp(new Array(6).fill(""));
        inputRefs.current[0]?.focus();
      } else {
        setSnackbar({
          open: true,
          message: res.data.message || "Failed to send OTP.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error sending OTP. Try again.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  // Helper to join OTP array to string
  const otpValue = otp.join("");

  // Handle OTP input change per box
  const handleOtpChange = (element, index) => {
    if (!/^\d?$/.test(element)) return; // only allow single digit number or empty

    const newOtp = [...otp];
    newOtp[index] = element;
    setOtp(newOtp);

    if (element && index < 5) {
      // Focus next input
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle key down for backspace navigation
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        if (index > 0) {
          inputRefs.current[index - 1].focus();
          const newOtp = [...otp];
          newOtp[index - 1] = "";
          setOtp(newOtp);
        }
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const verifyOtpAndTransfer = async () => {
    if (otpValue.length !== 6) {
      setSnackbar({
        open: true,
        message: "Enter valid 6-digit OTP.",
        severity: "error",
      });
      return;
    }
    if (recipientMobile === senderMobile) {
      setSnackbar({
        open: true,
        message: "Sender and recipient numbers cannot be the same.",
        severity: "error",
      });
      return;
    }

    if (recipientMobile.length !== 10) {
      setSnackbar({
        open: true,
        message: "Enter valid 10-digit recipient number.",
        severity: "error",
      });
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setSnackbar({
        open: true,
        message: "Enter valid amount.",
        severity: "error",
      });
      return;
    }
    if (recipientMobile !== reEnteredRecipientMobile) {
      setSnackbar({
        open: true,
        message: "Recipient mobile numbers do not match.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      // 1. Verify OTP
      const otpRes = await axios.post(`${api_url}otp/verify-otp`, {
        mobileNumber: senderMobile,
        otp: otpValue,
      });

      if (otpRes.data.success) {
        // 2. Call Transfer Points API after OTP verified
        const transferRes = await axios.post(
          `${api_url}wallet/transfer-points`,
          {
            senderMobile,
            recipientMobile,
            amount,
            otpVerified: true,
            modeOfMoney: receiveCash ? "receive_cash" : "points",
          },
          {
            headers: {
              accesstoken: sessionStorage.getItem("accesstoken"),
            },
          },
        );

        if (transferRes.data.success) {
          setSuccessDialogOpen(true); // Open dialog
          getWalletPoints();
          setRecipientMobile("");
          setReEnteredRecipientMobile("");
          setAmount("");
          setReceiveCash(false);
          setOtp(new Array(6).fill(""));
          setOtpSent(false);
          setOtpId(null);
          setOtpRemainingText("");
        } else {
          setSnackbar({
            open: true,
            message: transferRes.data.message || "Transfer failed.",
            severity: "error",
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: otpRes.data.message || "OTP verification failed.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error verifying OTP or transferring.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const getWalletPoints = async () => {
    const response = await axios.get(`${api_url}wallet/balance`, {
      headers: { accesstoken: sessionStorage.getItem("accesstoken") },
    });
    const request = response.data;
    setWalletBalance(request?.walletPoint?.balance);
    return response.data;
  };

  return (
    <Box maxWidth={900} sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        ID Wallet Transfer
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 0.5, fontWeight: "bold" }}>
                From <span style={{ color: "gray" }}>[ Dene Wala ]</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={senderMobile}
                placeholder="Sender Mobile Number"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                required
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        style={{
                          borderRight: "1px solid #ccc",
                          color: "#bdbdbd",
                          padding: 2,
                        }}
                      >
                        +91
                      </div>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 0.5, fontWeight: "bold" }}>
                Recipient Mobile Number{" "}
                <span style={{ color: "gray" }}>[ Lene Wala ]</span>{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                SMINDIA2050 Registered Mobile Number
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={recipientMobile}
                onChange={(e) => {
                  const val = e.target.value;
                  if (validateMobileNumber(val)) setRecipientMobile(val);
                }}
                placeholder="Enter 10-digit number"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                error={!!mobileError}
                helperText={mobileError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        style={{
                          borderRight: "1px solid #ccc",
                          padding: 2,
                          color: "#bdbdbd",
                        }}
                      >
                        +91
                      </div>
                    </InputAdornment>
                  ),
                  endAdornment: isValidMobile && (
                    <InputAdornment position="end">
                      <CheckIcon style={{ color: "green" }} />
                    </InputAdornment>
                  ),
                }}
                disabled={otpSent}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 0.5, fontWeight: "bold" }}>
                Re-Enter Recipient Mobile Number{" "}
                <span style={{ color: "gray" }}>[ Lene Wala ]</span>{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={reEnteredRecipientMobile}
                onChange={(e) => {
                  const val = e.target.value;
                  if (validateMobileNumber(val))
                    setReEnteredRecipientMobile(val);
                }}
                placeholder="Re-enter 10-digit number"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        style={{
                          borderRight: "1px solid #ccc",
                          padding: 2,
                          color: "#bdbdbd",
                        }}
                      >
                        +91
                      </div>
                    </InputAdornment>
                  ),
                }}
                disabled={otpSent}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Transfer Points <span style={{ color: "red" }}>*</span>
                </Typography>
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontWeight: 600 },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={receiveCash}
                      onChange={(event) => setReceiveCash(event.target.checked)}
                      disabled={otpSent}
                    />
                  }
                  label="Receive Cash"
                />
              </Box>
              <TextField
                fullWidth
                size="small"
                type="tel"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                value={amount}
                onChange={handleAmountChange}
                placeholder="Enter Points"
                required
                disabled={otpSent}
                error={!!amountError}
                helperText={amountError}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ color: "red" }}>
                * OTP will cost $ 0.105 *
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={sendOtp}
                disabled={loading || otpSent || !!amountError || !amount}
                sx={{ background: "#161b4d" }}
              >
                {otpSent ? "OTP Sent" : "Send OTP"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} mt={1} sx={{ display: "none" }}>
          {otpSent && (
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  border: "1px solid #ffeeba",
                  borderRadius: "8px",
                  padding: 2,
                  my: 2,
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                ⚠️ Please do not refresh or navigate away. This will cancel the
                transaction and invalidate the OTP.
              </Box>
            </Grid>
          )}

          {/* OTP input and verify button - only show if OTP sent */}
          {otpSent && (
            <>
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: 2,
                    textAlign: "center",
                    fontSize: "1rem",
                    fontWeight: 500,
                    display: "inline-flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 0.5,
                    wordBreak: "keep-all",
                    flexDirection: "column",
                  }}
                >
                  <Box>
                    <Typography component="span" sx={{ userSelect: "none" }}>
                      🔐 OTP sent from{" "}
                    </Typography>
                    <Typography
                      sx={{ userSelect: "text", fontWeight: "bold" }}
                      display="inline"
                    >
                      +1xxxxxxxx-United States
                    </Typography>
                    <Typography component="span">
                      {" "}
                      to your mobile number ending with{" "}
                    </Typography>
                    <Typography
                      display="inline"
                      sx={{
                        userSelect: "text",
                        fontWeight: "bold",
                        color: "#e65100",
                      }}
                    >
                      xxxxxx
                      {senderMobile?.slice(-4)}
                    </Typography>
                    <Typography component="span">
                      . (valid till next 15 minutes)
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ mt: 1, color: "#4caf50" }}>
                    ⏱️ Valid till: {otpRemainingText}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, mt: 2, textAlign: "center" }}
                >
                  Enter OTP <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    maxWidth: 360,
                    mx: "auto",
                  }}
                >
                  {otp?.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      inputProps={{
                        maxLength: 1,
                        style: {
                          textAlign: "center",
                          fontSize: "1.5rem",
                          padding: 0,
                        },
                      }}
                      sx={{
                        width: 48,
                        height: 56,
                        "& .MuiInputBase-root": {
                          borderRadius: "8px",
                        },
                      }}
                      autoComplete="one-time-code"
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <>
                  {resendCooldown > 0 ? (
                    <></>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={sendOtp}
                      disabled={loading || resendCooldown > 0}
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        color: "#1565c0",
                        borderColor: "#90caf9",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          borderColor: "#64b5f6",
                        },
                      }}
                    >
                      Resend OTP
                    </Button>
                  )}
                </>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={verifyOtpAndTransfer}
                  disabled={loading}
                  sx={{ background: "#1b5e20" }}
                >
                  Verify OTP & Transfer
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>

      <OtpVerificationDialog
        open={otpSent}
        senderMobile={senderMobile}
        otp={otp}
        onOtpChange={handleOtpChange}
        onOtpKeyDown={handleOtpKeyDown}
        inputRefs={inputRefs}
        onVerify={verifyOtpAndTransfer}
        onResend={sendOtp}
        canResend={resendCooldown <= 0}
        loading={loading}
        remainingTime={otpRemainingText}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <TransferSuccessDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
      />
    </Box>
  );
}

export default OnlineTransferSimple;
